'use client'

import { useState, useEffect, useCallback } from 'react'

interface LiveScoreData {
  home_score: number
  away_score: number
  status: string
  status_short: string
  elapsed: number | null
  is_live: boolean
  fixture_id: number
  last_updated: string
}

interface UseLiveScoreOptions {
  matchId: number
  id_api?: string | null
  match_status?: string
  home_score?: string | number
  away_score?: string | number
  autoRefresh?: boolean
  refreshInterval?: number
}

export function useLiveScore({
  matchId,
  id_api,
  match_status,
  home_score,
  away_score,
  autoRefresh = true,
  refreshInterval = 30000 // 30 seconds
}: UseLiveScoreOptions) {
  const [liveScoreData, setLiveScoreData] = useState<LiveScoreData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchLiveScore = useCallback(async () => {
    if (!id_api) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/football/fixture?fixtureId=${id_api}`)
      const result = await response.json()

      if (result.success && result.data) {
        const fixture = result.data.fixture
        setLiveScoreData({
          home_score: fixture?.goal_home !== null && fixture?.goal_home !== undefined ? fixture.goal_home : 0,
          away_score: fixture?.goal_away !== null && fixture?.goal_away !== undefined ? fixture.goal_away : 0,
          status: fixture?.status_long ?? '',
          status_short: fixture?.status_short ?? '',
          elapsed: fixture?.elapsed ?? null,
          is_live: ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'LIVE'].includes(fixture?.status_short ?? ''),
          fixture_id: parseInt(id_api),
          last_updated: new Date().toISOString()
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch live score')
    } finally {
      setLoading(false)
    }
  }, [id_api])

  // Get current score (prioritize live data over database data)
  const getCurrentScore = useCallback(() => {
    if (liveScoreData) {
      return {
        home_score: liveScoreData.home_score,
        away_score: liveScoreData.away_score,
        is_live: liveScoreData.is_live,
        status: liveScoreData.status,
        status_short: liveScoreData.status_short,
        elapsed: liveScoreData.elapsed
      }
    }

    return {
      home_score: Number(home_score) || 0,
      away_score: Number(away_score) || 0,
      is_live: match_status === '3',
      status: match_status ?? '',
      status_short: '',
      elapsed: null
    }
  }, [liveScoreData, home_score, away_score, match_status])

  // Initial fetch
  useEffect(() => {
    // Fetch API Football data if:
    // 1. Match is live (status '3') and has id_api
    // 2. OR match is finished (status '2') and has id_api
    // 3. OR match has id_api but database scores are empty (0 or null)
    const shouldFetchApiFootball = id_api && (
      match_status === '3' || // Live match
      match_status === '2' || // Finished match
      (!home_score || home_score === '0' || !away_score || away_score === '0') // Empty scores
    )

    if (shouldFetchApiFootball) {
      fetchLiveScore()
    }
  }, [fetchLiveScore, id_api, match_status, home_score, away_score])

  // Auto refresh for live matches
  useEffect(() => {
    if (!autoRefresh || match_status !== '3' || !id_api) {
      return
    }

    const interval = setInterval(() => {
      fetchLiveScore()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [autoRefresh, match_status, id_api, refreshInterval, fetchLiveScore])

  return {
    liveScoreData,
    loading,
    error,
    getCurrentScore,
    refetch: fetchLiveScore
  }
} 